import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import classNames from 'clsx';
import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import AMLButtonGroup from '@swf/components/AMLButtonGroup/AMLButtonGroup';
import ResolverIframe from '@swf/components/Iframe/ResolverIframe';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { closeDocVerify } from '@swf/core/utils/postMessages';

import { sendStartVerificationRequest } from '../SumSubProvider/utils';

import ConfirmationMessage from './ConfirmationMessage/ConfirmationMessage';
import DocumentInformationBox from './DocumentInformationBox/DocumentInformationBox';
import IDScan from './IDScan/IDScan';
import { InputProvider } from './IDScan/types';
import {
  TXTCLI_Cancel,
  TXTCLI_Confirm,
  TXTCLI_OK,
  TXTCLI_Please_try_again_laterX,
  TXTCLI_Select_which_document_youXd_li_ELL,
  TXTCLI_Verification_Failed,
  TXTCLI_We_apologize_for_any_inconveni_ELL_1,
  TXTCLI_We_just_need_to_verify_your_id_ELL,
  TXTWCL_If_you_have_any_issuesX_please_ELL,
  TXTWCL_SorryX_we_canXt_verify_your_do_ELL,
  TXTWCL_You_have_recently_completed_th_ELL,
  TXTWCL_You_will_need_to_enable_your_c_ELL
} from './IDScanProvider.messages.json';
import styles from './IDScanProvider.scss';
import VerifyAccountInformationMessages from './InformationMessage/InformationMessage';

enum VerifyStep {
  Cancel,
  Confirm,
  ContactSupportForm,
  Error,
  IDScan,
  Initial,
  Result,
  Submitted,
  BlockingError
}

interface Props {
  className?: string;
  isModal?: boolean;
  onClose?(): void;
  openIDScan?: boolean;
  handleCancel?(): void;
  showButton?: boolean;
  onComplete?(): void;
  onJourneyComplete?(): void;
  onJourneyInitialized?(): void;
  onJourneyRefId?: (jid: string | undefined) => void;
  rowLayout?: boolean;
  infoMessage?: React.ReactElement;
  titleClassName?: string;
  title?: TranslationTag;
}

const IDScanProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  handleCancel,
  isModal,
  onClose,
  onComplete,
  openIDScan,
  showButton,
  onJourneyComplete,
  onJourneyInitialized,
  onJourneyRefId,
  rowLayout,
  infoMessage,
  titleClassName,
  title
}) => {
  const IDScanSDK = useFeature(FeatureFlag.IDScanSDK);
  const IDScanHosted = useFeature(FeatureFlag.IDScanHosted);
  const amlVerified = useSelector(select.account.amlVerified);
  const [verifyStep, setVerifyStep] = useState(VerifyStep.Initial);
  const { documentsSubmitted } = useSelector(select.localSettings.get);
  const amlDocsInProgress = useSelector(select.account.amlDocsInProgress);
  const [defaultInputProvider, setDefaultInputProvider] = useState<InputProvider>();
  const [translationErrorMessage, setTranslationErrorMessage] = useState<ReactElement | null>(null);
  const request = useRequest();

  const { modal } = useDispatch<Dispatch>();

  const {
    showCancelButton,
    showContinueButton,
    showError,
    showFileSystem,
    showSmartCapture,
    verifyInitialStep
  } = useMemo(
    () => ({
      showCancelButton: () => setVerifyStep(VerifyStep.Cancel),
      showContinueButton: () => setVerifyStep(VerifyStep.Confirm),
      showFileSystem: () => setDefaultInputProvider(InputProvider.FILESYSTEM),
      showSmartCapture: () => setDefaultInputProvider(InputProvider.SMART_CAPTURE),
      verifyInitialStep: () => {
        if (openIDScan) {
          setVerifyStep(IDScanSDK ? VerifyStep.IDScan : VerifyStep.ContactSupportForm);
          return;
        }
        setVerifyStep(documentsSubmitted ? VerifyStep.Submitted : VerifyStep.Initial);
      },
      showError: (error?: ReactElement, isBlocking?: boolean) => {
        const step = isBlocking ? VerifyStep.BlockingError : VerifyStep.Error;

        if (error) {
          setTranslationErrorMessage(error);
        }

        setVerifyStep(step);
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onUploadClick = useCallback(() => {
    setVerifyStep(IDScanSDK || IDScanHosted ? VerifyStep.IDScan : VerifyStep.ContactSupportForm);
    sendStartVerificationRequest(
      {
        request
      },
      IDScanSDK ? 'IdScan' : undefined
    );
  }, [IDScanSDK, IDScanHosted, request]);

  const closeModal = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    closeDocVerify();
    modal.close('/auth/docverify');
  }, [modal, onClose]);

  useEffect(verifyInitialStep, [verifyInitialStep]);

  return (
    <div className={classNames(styles.container, className, { [styles.modal]: isModal })}>
      <div className={classNames(styles.wrapper, { [styles.modal]: isModal })}>
        {[VerifyStep.Submitted, VerifyStep.Initial].includes(verifyStep) && (
          <>
            {verifyStep === VerifyStep.Submitted ? (
              <StarsInfo className={sharedStyles.infoMessage}>
                <Translation {...TXTWCL_You_have_recently_completed_th_ELL} />
              </StarsInfo>
            ) : children ? (
              children
            ) : (
              <>
                <Translation {...TXTCLI_We_just_need_to_verify_your_id_ELL} />{' '}
                <Translation {...TXTWCL_You_will_need_to_enable_your_c_ELL} />{' '}
                <Translation {...TXTWCL_If_you_have_any_issuesX_please_ELL} />
                {(amlDocsInProgress || amlVerified) &&
                  (infoMessage || <VerifyAccountInformationMessages amlVerified={amlVerified} />)}
              </>
            )}

            <p className={classNames(styles.subTitle, titleClassName)}>
              <Translation {...(title || TXTCLI_Select_which_document_youXd_li_ELL)} />
            </p>
            <AMLButtonGroup rowLayout={rowLayout}>
              {(documentType) =>
                documentType !== undefined ? (
                  <DocumentInformationBox
                    documentType={documentType}
                    onUploadClick={onUploadClick}
                    showFileSystem={showFileSystem}
                    showSmartCapture={showSmartCapture}
                  />
                ) : null
              }
            </AMLButtonGroup>
          </>
        )}
        {[VerifyStep.Error, VerifyStep.BlockingError].includes(verifyStep) && (
          <div>
            <StarsInfo type="error">
              {translationErrorMessage ? (
                translationErrorMessage
              ) : (
                <>
                  <Translation tagName="b" {...TXTCLI_Verification_Failed} />
                  <Translation tagName="p" {...TXTWCL_SorryX_we_canXt_verify_your_do_ELL} />
                  <Translation {...TXTCLI_We_apologize_for_any_inconveni_ELL_1} />
                  <Translation tagName="p" {...TXTCLI_Please_try_again_laterX} />
                </>
              )}
            </StarsInfo>
            {(isModal || showButton) && (
              <StarsButton
                block
                onClick={closeModal}
                translation={TXTCLI_OK}
                className={styles.okButton}
              />
            )}
          </div>
        )}
        {verifyStep === VerifyStep.ContactSupportForm && (
          <ResolverIframe
            scrolling="auto"
            tag={ResolverTag.VerifyMeAML}
            className={classNames(styles.contactSupportForm, { [styles.modal]: isModal })}
          />
        )}
        {[VerifyStep.IDScan, VerifyStep.Cancel, VerifyStep.Confirm].includes(verifyStep) && (
          <>
            <IDScan
              showError={showError}
              onJourneyStart={showCancelButton}
              onJourneyComplete={showContinueButton}
              defaultInputProvider={defaultInputProvider}
              onJourneyInitialized={onJourneyInitialized}
              onJourneyRefId={onJourneyRefId}
            />
            {verifyStep === VerifyStep.Cancel && (
              <StarsButton
                block
                variant="secondary"
                translation={TXTCLI_Cancel}
                onClick={handleCancel || verifyInitialStep}
                className={styles.cancelButton}
              />
            )}
            {verifyStep === VerifyStep.Confirm && (
              <StarsButton
                block
                translation={TXTCLI_Confirm}
                className={sharedStyles.message}
                onClick={() => {
                  setVerifyStep(VerifyStep.Result);
                  onJourneyComplete?.();
                }}
              />
            )}
          </>
        )}
        {verifyStep === VerifyStep.Result && (
          <ConfirmationMessage onComplete={onComplete} showButton={showButton || isModal} />
        )}
      </div>
    </div>
  );
};

export default IDScanProvider;
